interface AssetEntry {
  public_id?: string;
  original_filename?: string;
  format?: string;
  secure_url?: string;
  original_secure_url?: string;
  url?: string;
}

// A single transformation parameter, e.g. "c_auto" or "ar_1:1".
const TRANSFORM_TOKEN = /^[a-z]+_[^,/]+$/;
// A delivery version segment, e.g. "v1623456789".
const VERSION_SEGMENT = /^v\d+$/;
// Delivery tokens (f_ = format, q_ = quality) are managed separately from crop
// transforms and must not be mixed into transform segments.
const DELIVERY_TOKEN = /^[fq]_/;

// Cloudinary rejects transformations whose intermediate result exceeds the
// account's 50 MP limit. Named Transforms crop (c_fill + ar_*) at full source
// resolution before w_* is applied, so e.g. a 1:1 crop of an 8736×11648 upload
// is 76 MP and fails with 400 "Maximum image size is 50 Megapixels". Capping the
// source to 7000×7000 (49 MP) first keeps every crop under the limit while
// leaving enough resolution for the largest srcset width.
const SIZE_GUARD_SEGMENT = "c_limit,h_7000,w_7000";

function isDeliveryToken(token: string): boolean {
  return DELIVERY_TOKEN.test(token);
}

function isDeliverySegment(segment: string): boolean {
  return segment.split(",").every(isDeliveryToken);
}

function isTransformSegment(segment: string): boolean {
  return (
    !isDeliverySegment(segment) &&
    segment.split(",").every((token) => TRANSFORM_TOKEN.test(token))
  );
}

// Inserts crop/effect tokens into a Cloudinary URL as the last chained
// transformation before the version/public ID, then appends f_auto and q_auto
// as separate delivery segments. Any f_auto/q_auto already in the source URL are
// stripped to avoid duplication. Order matters: crop runs before resize, which
// runs before delivery.
function insertCloudinaryTransform(
  url: string,
  tokens: string[]
): string | null {
  const UPLOAD_SEGMENT = "/upload/";
  const idx = url.indexOf(UPLOAD_SEGMENT);

  if (idx === -1) {
    return null;
  }

  const insertAt = idx + UPLOAD_SEGMENT.length;
  const prefix = url.slice(0, insertAt);
  // Strip any existing delivery segments — we always re-append them after our transform.
  const segments = url
    .slice(insertAt)
    .split("/")
    .filter((seg) => !isDeliverySegment(seg));

  // Skip past existing crop/effect transform and version segments to find the public ID.
  let publicIdStart = 0;
  while (
    publicIdStart < segments.length &&
    (isTransformSegment(segments[publicIdStart]) ||
      VERSION_SEGMENT.test(segments[publicIdStart]))
  ) {
    publicIdStart++;
  }

  // Insert after the last transform but before any version segment.
  let insertIndex = publicIdStart;
  while (insertIndex > 0 && VERSION_SEGMENT.test(segments[insertIndex - 1])) {
    insertIndex--;
  }

  // Named Transform (t_*) and width (w_*) share one URL segment.
  // Other tokens (e.g. g_custom) become a separate segment placed after.
  // → t_d2c_tall,w_1536/g_custom   (Cloudinary rejects t_name,g_custom in the same segment)
  // If a Named Transform already exists in the URL, replace it instead of chaining.
  const namedTokens = tokens.filter((t) => t.startsWith("t_"));
  const widthTokens = tokens.filter((t) => t.startsWith("w_"));
  const otherTokens = tokens.filter(
    (t) => !(t.startsWith("t_") || t.startsWith("w_"))
  );
  const mainTokens = [...namedTokens, ...widthTokens];

  const existingNamedIndex =
    namedTokens.length > 0
      ? segments
          .slice(0, insertIndex)
          .findIndex((seg) => seg.split(",").some((t) => t.startsWith("t_")))
      : -1;

  if (existingNamedIndex >= 0) {
    segments[existingNamedIndex] = mainTokens.join(",");
    const extra = otherTokens.length > 0 ? [otherTokens.join(",")] : [];
    segments.splice(insertIndex, 0, ...extra, "f_auto", "q_auto");
    if (segments[existingNamedIndex - 1] !== SIZE_GUARD_SEGMENT) {
      segments.splice(existingNamedIndex, 0, SIZE_GUARD_SEGMENT);
    }
  } else if (mainTokens.length > 0) {
    const extra = otherTokens.length > 0 ? [otherTokens.join(",")] : [];
    const guard = namedTokens.length > 0 ? [SIZE_GUARD_SEGMENT] : [];
    segments.splice(
      insertIndex,
      0,
      ...guard,
      mainTokens.join(","),
      ...extra,
      "f_auto",
      "q_auto"
    );
  } else {
    const newSegments: string[] = tokens.length > 0 ? [tokens.join(",")] : [];
    segments.splice(insertIndex, 0, ...newSegments, "f_auto", "q_auto");
  }

  return `${prefix}${segments.join("/")}`;
}

// Default candidate widths for a responsive srcset, in CSS pixels.
// biome-ignore lint/style/noMagicNumbers: srcset candidate widths
const DEFAULT_SRCSET_WIDTHS = [480, 768, 1024, 1536, 2048, 3072];
const DEFAULT_SRCSET_WIDTH = 768;

interface CloudinarySrcSetOptions {
  // Crop/effect tokens applied to every variant, e.g. ["ar_1:1", "c_auto", "g_auto"]
  // or a named transformation ["t_d2c_square"]. The width is appended per variant.
  // Do NOT include f_auto/q_auto — insertCloudinaryTransform appends them automatically.
  transform: string[] | string;
  // Candidate widths; the browser picks one via the <img> sizes attr and DPR.
  widths?: number[];
  // Width used for the non-srcset `src` fallback.
  defaultWidth?: number;
}

// Builds a responsive rendition (src + srcSet) by appending each width to the
// given transform. The transform is inserted after any baked-in editor crops
// and before the version/public ID (see insertCloudinaryTransform), so it works
// with both inline transforms and Cloudinary named transformations.
export function getCloudinarySrcSet(
  url: string,
  {
    transform,
    widths = DEFAULT_SRCSET_WIDTHS,
    defaultWidth = DEFAULT_SRCSET_WIDTH,
  }: CloudinarySrcSetOptions
): { src: string; srcSet: string } | null {
  if (!url) {
    return null;
  }

  const variants = widths
    .map((width) => {
      let transformations: string[] = [];
      if (Array.isArray(transform)) {
        transformations = [...transform, `w_${width}`];
      } else {
        transformations = [transform, `w_${width}`];
      }

      const transformed = insertCloudinaryTransform(url, transformations);

      return transformed ? { url: transformed, width } : null;
    })
    .filter((variant): variant is { url: string; width: number } => !!variant);

  // Empty only when the URL has no /upload/ segment (no variant was built).
  const fallback = variants.at(-1);
  if (!fallback) {
    return null;
  }

  const src = (variants.find((v) => v.width === defaultWidth) ?? fallback).url;
  const srcSet = variants.map((v) => `${v.url} ${v.width}w`).join(", ");

  return { src, srcSet };
}

import { CLOUDINARY_TRANSFORMATION_D2C_SQUARE } from "./cloudinary-transforms";

// Convenience wrapper: an always-square, smart-cropped responsive rendition.
export function getCloudinarySquareSrcSet(
  url: string
): { src: string; srcSet: string } | null {
  return getCloudinarySrcSet(url, {
    transform: CLOUDINARY_TRANSFORMATION_D2C_SQUARE,
  });
}

/**
 * Extracts the first available asset URL from a data array.
 * Tries secure URLs first, then falls back to regular URLs.
 */
export function extractCloudinaryAssetUrl(data: unknown): string {
  if (!Array.isArray(data) || data.length === 0) {
    return "";
  }

  for (const entry of data) {
    if (!entry || typeof entry !== "object") {
      continue;
    }

    const assetEntry = entry as AssetEntry;

    let url = "";

    // URLs in order of preference
    if (assetEntry.secure_url) {
      url = assetEntry.secure_url;
    } else if (assetEntry.original_secure_url) {
      url = assetEntry.original_secure_url;
    } else if (assetEntry.url) {
      url = assetEntry.url;
    }

    if (!url.includes("https://")) {
      url = url.replace("http://", "https://");
    }

    return url;
  }

  return "";
}

/**
 * Name of the first Cloudinary asset for analytics: the uploaded filename
 * (e.g. "gravel-body-1.jpg") when known, otherwise the public_id.
 */
export function extractCloudinaryAssetName(data: unknown): string | undefined {
  const entry = Array.isArray(data) ? data[0] : undefined;

  if (!entry || typeof entry !== "object") {
    return;
  }

  const asset = entry as AssetEntry;

  if (asset.original_filename) {
    return asset.format
      ? `${asset.original_filename}.${asset.format}`
      : asset.original_filename;
  }

  return asset.public_id || undefined;
}

/**
 * Analytics creative name of a banner: the first media item with a Cloudinary
 * image (or video poster), as its filename or public_id.
 */
export function extractMediaCreativeName(
  items:
    | ReadonlyArray<{ image?: unknown; poster?: unknown } | null>
    | null
    | undefined
): string | undefined {
  for (const item of items ?? []) {
    const name =
      extractCloudinaryAssetName(item?.image) ??
      extractCloudinaryAssetName(item?.poster);

    if (name) {
      return name;
    }
  }
}
